import React, {useEffect, useState} from "react";
import {connect, useDispatch} from "react-redux";
import Form from "./ScanBarcodeForm";
import {cancelScanBarcode} from "../../../actions/InventoryApiActions";
import {validatePresence} from "../../../lib/Validators";
import {Map} from "immutable";
import {getPackageUID, withWarehousePackages} from "../../../lib/WarehouseHelper";
import OrderBasicInfo from "../../../dashboard/views/warehouses/OrderBasicInfo";
import {updatePackages} from "../../../actions/WarehousePackageActions";
import {ASSIGN_SERIAL_NRS} from "../../../queries/warehouseJobs";
import {useMutation} from "react-apollo";
import styles from "./ScanBarcodeForm.module.scss";
import ActionButton from "../../../components/ActionButton";
import Checked from "../../../components/shared/icons/Checked";
import Error from "../../../components/shared/icons/Error";
import LoadingCircle from "../../../components/LoadingCircle";


const STATES = {
  INITIAL: "initial",
  VALIDATING: "validating",
  ERROR: "error",
  SUCCESS: "success",
}

const ScanBarcodeFormContainer = (props) => {
  const { warehouseId, packageId } = props;

  const dispatch = useDispatch();
  const [state, setState] = useState(STATES.INITIAL);

  const pack = props.warehousePackages.find((p) => getPackageUID(p) === packageId);
  const job = props.getJobById(pack.get("jobId"));

  const [assignSerialNrs, { data, loading, error }] = useMutation(ASSIGN_SERIAL_NRS);

  const indexOfSkuToEdit = pack.get("skus").findIndex((s) => s.get("serial") !== "not_applicable");
  if (indexOfSkuToEdit === -1) {
    // should never happen as those items don't get the "Scan" button.
    console.log("Couldn't find a sku suitable for serial number.")
  }

  // Init saved barcode with the current "serialNrs", if any
  const [savedBarcodes, saveBarcodes] = useState(pack.getIn(["skus", indexOfSkuToEdit, "serialNrs"], []));

  useEffect(() => {
    if (loading) {
      setState(STATES.VALIDATING);
    } else if (error) {
      setState(STATES.ERROR);
    } else if (data) {
      // Update packages in the reducer
      dispatch(updatePackages(warehouseId, [getPackageUID(pack)], (p) =>
        p.setIn(["skus", indexOfSkuToEdit, "serialNrs"], savedBarcodes)
         .setIn(["skus", indexOfSkuToEdit, "serial"], "provided")
        , true));
      setState(STATES.SUCCESS);
    }
  }, [data, loading, error]);

  const resetState = () => {
    setState(STATES.INITIAL);
  }

  return (
    <>
      <OrderBasicInfo warehouseId={warehouseId} name={job.recipientName} jobId={pack.get("jobId")} />
      <SecondaryLine label="PACKAGE NUMBER" value={pack.get("id")} />
      <SecondaryLine label="TRACKING NUMBER" value={pack.get("trackingNr")} />
      <SecondaryLine label="SKU" value={pack.getIn(["skus", indexOfSkuToEdit, "sku"])} />
      <SecondaryLine label="SERIAL NUMBER"  value={savedBarcodes.join(", ")} />
      <div>
        {
          // handle each possible state
          {
            [STATES.INITIAL]: <ScanBarcodeFormInner warehouseId={warehouseId} pack={pack} assignSerialNrs={assignSerialNrs} saveBarcodes={saveBarcodes} />,
            [STATES.VALIDATING]: (
              <div className={styles.validating}>
                <LoadingCircle color="var(--grey-400)" size={LoadingCircle.SIZES.SMALL} noMargin />
                <span className={styles.upperedText}>Validating Barcode with Serial Number</span>
              </div>
            ),
            [STATES.ERROR]: (
              <div className={styles.error}>
                <Error side="33px" />
                <span className={styles.upperedText}>There is a mismatch between barcode and serial number, please revise and scan the correct code.</span>
                <span className={styles.spacer} />
                <span className={styles.upperedText}>Entered Barcode Number</span>
                <span className={styles.datum}>{ savedBarcodes }</span>
                <span className={styles.upperedText}>Matched with</span>
                <span className={styles.datum}>{ pack.getIn(["skus", indexOfSkuToEdit, "sku"]) }</span>

                <ActionButton label="Scan Barcode Again" onClick={resetState} />
              </div>
            ),
            [STATES.SUCCESS]: (
              <div className={styles.success}>
                <Checked side="33px" />
                <span className={styles.upperedText}>Correctly Verified</span>

                <ActionButton label="Close" onClick={props.cancelScanBarcode} />
              </div>
            ),
          }[state]
        }
      </div>
    </>
  );
}


function SecondaryLine(props) {
  const { label, value } = props;

  if (!label || !value) {
    return null;
  }

  return (
    <div className="bg-secondary">
      <div className="sg-inspector-title sg-font-normal sg-grey-400">{ label }</div>
      <div className="sg-font-bold sg-code-text sg-grey-800">{ value }</div>
    </div>
  );
}

const ScanBarcodeFormInner = (props) => {
  const { warehouseId, pack, assignSerialNrs, saveBarcodes } = props;

  const [errors] = useState([]);

  const validateForm = (values) => {
    let errors = Map();
    errors = validatePresence(errors, values, "barcode", "Please input a barcode");
    return errors.toJS();
  }
  const handleSubmit = (data) => {
    const barcode = data.get("barcode");
    if (!barcode) return;

    // Save barcodes for reuse in other STATES
    saveBarcodes([barcode]);

    const skus = pack.get("skus").filter((s) => s.get("serial") !== "not_applicable");
    if (skus.size !== 1 || skus.getIn([0,"quantity"]) !== 1) {
      // Shouldn't happen
      // TODO: have n barcodes when quantity > 1
      console.log("We can only handle one sku and one item for the barcode per package");
      return;
    }

    const sku = skus.getIn([0,"sku"]);

    // Send mutation to server
    const mutationVariables = {
      variables: {
        data: {
          warehouseId: warehouseId,
          jobId: pack.get("jobId"),
          packageId: pack.get("packageId"),
          sku: sku,
          serialNrs: [barcode]
        }
      }
    };
    assignSerialNrs(mutationVariables);
  };

  return (
    <>
      <span className={styles.upperedText}>Scan or enter barcode</span>
      <Form
        initialValues={null}
        errors={errors}
        validate={validateForm}
        onSubmit={handleSubmit}
      />
    </>
  );
}


export default connect(null, {cancelScanBarcode})(withWarehousePackages(ScanBarcodeFormContainer));
